'use client';

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  HelpOutline as HelpOutlineIcon,
  MoreHoriz as MoreIcon,
  CreditCard as CreditCardIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import Link from 'next/link';
import { signOut } from 'next-auth/react';
import { useTheme } from '@mui/material/styles';
import { useLocale, useTranslations } from 'next-intl';
import { CompactDarkModeToggle } from '@/components/ui/DarkModeToggle';
import { Logo } from '@/components/ui/Logo';
import { performLogout } from '@/lib/auth-helpers';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { getUiCopy } from '@/i18n/uiCopy';

interface AppHeaderProps {
  /**
   * If true, shows authenticated navigation (dashboard, profile, etc.)
   * If false, shows public navigation (sign in, get started)
   */
  authenticated?: boolean;
  user?: {
    name?: string | null;
    email?: string | null;
  } | null;
}

// Primary navigation items (always visible on desktop)
const primaryNavLinks = [
  { key: 'dashboard', href: '/dashboard', icon: DashboardIcon },
  { key: 'discover', href: '/listings', icon: SearchIcon },
  { key: 'events', href: '/events', icon: EventIcon },
] as const;

// Secondary navigation items (in dropdown menu on medium screens)
const secondaryNavLinks = [
  { key: 'community', href: '/community', icon: PeopleIcon },
  { key: 'messages', href: '/messages', icon: MessageIcon },
] as const;

// All navigation items for mobile drawer
const allNavLinks = [...primaryNavLinks, ...secondaryNavLinks] as const;

export function AppHeader({ authenticated = false, user }: AppHeaderProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const locale = useLocale();
  const t = useTranslations('nav');
  const copy = getUiCopy(locale);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [navMenuAnchor, setNavMenuAnchor] = useState<null | HTMLElement>(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };
  const handleNavMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNavMenuAnchor(event.currentTarget);
  };
  const handleNavMenuClose = () => {
    setNavMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await performLogout(signOut);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: isDark ? 'rgba(10, 14, 26, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg" className="px-3 sm:px-4 lg:px-6">
        <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 }, py: { xs: 0.5, sm: 1 } }}>
          {/* Logo */}
          <Box className="flex items-center flex-grow">
            <Logo size="small" showText={true} href="/" />
          </Box>

          {authenticated ? (
            <>
              {/* Authenticated Navigation - Desktop */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}
              >
                {/* Primary navigation items */}
                {primaryNavLinks.map((link) => (
                  <Button
                    key={link.href}
                    component={Link}
                    href={link.href}
                    startIcon={<link.icon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 1.5,
                      py: 0.75,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {t(link.key)}
                  </Button>
                ))}

                {/* Secondary navigation items in dropdown */}
                <IconButton
                  onClick={handleNavMenuOpen}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  aria-label="more navigation"
                >
                  <MoreIcon />
                </IconButton>
              </Stack>

              {/* Navigation Dropdown Menu */}
              <Menu
                anchorEl={navMenuAnchor}
                open={Boolean(navMenuAnchor)}
                onClose={handleNavMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 180,
                    borderRadius: 2,
                  },
                }}
              >
                {secondaryNavLinks.map((link) => (
                  <MenuItem
                    key={link.href}
                    component={Link}
                    href={link.href}
                    onClick={handleNavMenuClose}
                    sx={{ py: 1.25 }}
                  >
                    <link.icon fontSize="small" sx={{ mr: 1.5 }} />
                    {t(link.key)}
                  </MenuItem>
                ))}
              </Menu>

              <Stack direction="row" spacing={1} alignItems="center">
                <LanguageSwitcher variant="icon" />
                <CompactDarkModeToggle />

                {/* Profile Avatar - Desktop */}
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={{ display: { xs: 'none', md: 'flex' }, p: 0.5 }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                    }}
                  >
                    {user?.email?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>

                {/* Mobile Hamburger */}
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label={copy.header.menuAriaLabel}
                  onClick={toggleMobileMenu}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    minWidth: 44,
                    minHeight: 44,
                    color: 'text.primary',
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>

              {/* Profile Menu - Desktop */}
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2,
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.name || copy.header.userFallback}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem
                  component={Link}
                  href="/dashboard"
                  onClick={handleProfileMenuClose}
                  sx={{ py: 1.25 }}
                >
                  <PersonIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {t('profile')}
                </MenuItem>
                <MenuItem
                  component={Link}
                  href="/account/subscription"
                  onClick={handleProfileMenuClose}
                  sx={{ py: 1.25 }}
                >
                  <CreditCardIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {copy.header.subscription}
                </MenuItem>
                <MenuItem
                  component={Link}
                  href="/pricing"
                  onClick={handleProfileMenuClose}
                  sx={{ py: 1.25 }}
                >
                  <UpgradeIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {copy.header.pricing}
                </MenuItem>
                <MenuItem
                  component={Link}
                  href="/help"
                  onClick={handleProfileMenuClose}
                  sx={{ py: 1.25 }}
                >
                  <HelpOutlineIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {copy.header.helpSupport}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.25, color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                  {t('signOut')}
                </MenuItem>
              </Menu>

              {/* Mobile Drawer - Authenticated */}
              {/* Mobile Drawer - Authenticated */}
              <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={toggleMobileMenu}
                PaperProps={{
                  sx: {
                    width: '100%',
                    maxWidth: 320,
                    bgcolor: isDark ? 'rgba(10, 14, 26, 0.98)' : 'background.paper',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: 'primary.main',
                          fontSize: '1rem',
                        }}
                      >
                        {user?.email?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={600} noWrap>
                          {user?.name || copy.header.userFallback}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.85rem' }}>
                          {user?.email}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={toggleMobileMenu}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  {/* Navigation Links */}
                  <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
                    <Stack spacing={0.5} sx={{ px: 1 }}>
                      {allNavLinks.map((link) => (
                        <Button
                          key={link.href}
                          component={Link}
                          href={link.href}
                          onClick={toggleMobileMenu}
                          fullWidth
                          startIcon={<link.icon />}
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '1rem',
                            px: 2,
                            py: 1.5,
                            minHeight: 52,
                            borderRadius: 2,
                            color: 'text.primary',
                            '&:active': { bgcolor: 'action.selected' },
                          }}
                        >
                          {t(link.key)}
                        </Button>
                      ))}
                    </Stack>

                    <Divider sx={{ my: 1.5, mx: 2 }} />

                    {/* Account Links */}
                    <Stack spacing={0.5} sx={{ px: 1 }}>
                      <Button
                        component={Link}
                        href="/account/subscription"
                        onClick={toggleMobileMenu}
                        fullWidth
                        startIcon={<CreditCardIcon />}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '1rem',
                          px: 2,
                          py: 1.5,
                          minHeight: 52,
                          borderRadius: 2,
                          color: 'text.primary',
                          '&:active': { bgcolor: 'action.selected' },
                        }}
                      >
                        {copy.header.subscription}
                      </Button>
                      <Button
                        component={Link}
                        href="/pricing"
                        onClick={toggleMobileMenu}
                        fullWidth
                        startIcon={<UpgradeIcon />}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '1rem',
                          px: 2,
                          py: 1.5,
                          minHeight: 52,
                          borderRadius: 2,
                          color: 'text.primary',
                          '&:active': { bgcolor: 'action.selected' },
                        }}
                      >
                        {copy.header.pricing}
                      </Button>
                      <Button
                        component={Link}
                        href="/help"
                        onClick={toggleMobileMenu}
                        fullWidth
                        startIcon={<HelpOutlineIcon />}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '1rem',
                          px: 2,
                          py: 1.5,
                          minHeight: 52,
                          borderRadius: 2,
                          color: 'text.primary',
                          '&:active': { bgcolor: 'action.selected' },
                        }}
                      >
                        {copy.header.helpSupport}
                      </Button>
                    </Stack>
                  </Box>

                  {/* Sign Out - Fixed at bottom */}
                  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      fullWidth
                      onClick={handleLogout}
                      startIcon={<LogoutIcon />}
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '1rem',
                        px: 2,
                        py: 1.5,
                        minHeight: 52,
                        borderRadius: 2,
                        color: 'error.main',
                        '&:active': { bgcolor: 'error.light' },
                      }}
                    >
                      {t('signOut')}
                    </Button>
                  </Box>
                </Box>
              </Drawer>
            </>
          ) : (
            <>
              {/* Public Navigation - Desktop */}
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                <LanguageSwitcher variant="icon" />
                <CompactDarkModeToggle />
                <Button
                  href="/auth/login"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 0.75,
                  }}
                >
                  {t('signIn')}
                </Button>
                <Button
                  href="/auth/login"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2.5,
                    py: 0.75,
                    borderRadius: 2,
                  }}
                >
                  {t('signUp')}
                </Button>
              </Stack>

              {/* Public Navigation - Mobile */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ display: { xs: 'flex', sm: 'none' } }}
              >
                <LanguageSwitcher variant="icon" />
                <CompactDarkModeToggle />
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label={copy.header.menuAriaLabel}
                  onClick={toggleMobileMenu}
                  sx={{ minWidth: 44, minHeight: 44, color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>

              {/* Mobile Drawer - Public */}
              <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={toggleMobileMenu}
                PaperProps={{
                  sx: {
                    width: '100%',
                    maxWidth: 320,
                    bgcolor: isDark ? 'rgba(10, 14, 26, 0.98)' : 'background.paper',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Logo size="small" showText={true} href="/" />
                    <IconButton
                      onClick={toggleMobileMenu}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3 }}>
                    <Stack spacing={2}>
                      <Button
                        href="/auth/login"
                        fullWidth
                        variant="outlined"
                        onClick={toggleMobileMenu}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          py: 1.5,
                          minHeight: 52,
                          borderRadius: 2,
                        }}
                      >
                        {t('signIn')}
                      </Button>
                      <Button
                        href="/auth/login"
                        variant="contained"
                        fullWidth
                        onClick={toggleMobileMenu}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          py: 1.5,
                          minHeight: 52,
                          borderRadius: 2,
                        }}
                      >
                        {t('signUp')}
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
